// DirectWalletAdjustModal — admin "God-Mode" credit / debit on a
// single player or single agent wallet, without an end-user-initiated
// request feeding the queue.
//
// FLAW 2 fix (2026-05-16): the previous list-card kebab had no direct
// recharge / withdraw action — admin had to wait for the player or
// agent to submit a recharge request, then approve it from the queue.
// For genuine "I just collected cash in person" or "I need to claw a
// mistaken credit back" scenarios that round-trip was operationally
// broken. This modal restores the superpower.
//
// ─── Two subject modes ────────────────────────────────────────────
//   subject='player'  — the modal targets a player. Two SOURCES are
//     possible, picked by the admin via a toggle:
//
//       Platform — admin mints/burns to/from the player's wallet
//         directly. No agent involvement. Backend:
//           POST /admin/users/:id/wallet
//             body { delta, reason, markAsRecharge: true (credit) }
//           (Same call WalletModel.adminAdjust has always served.)
//
//       Agent — the player's PARENT agent's pool funds the credit
//         (or absorbs the debit, when we ever wire that — see note).
//         Atomic two-leg transfer: debit agent pool, credit player.
//         Backend:
//           POST /admin/agents/{player.agent_id}/players/{player.id}/recharge
//             body { amount, note }
//         Only credit is supported here today (the existing endpoint
//         is one-way). Debit-via-agent is NOT implemented yet — when
//         admin picks Agent + Debit we surface a clear "Use Platform
//         debit + a separate agent topup" hint rather than failing.
//
//   subject='agent'   — the modal targets an agent's own pool. No
//     source toggle (admin is implicitly the counterparty). Two
//     endpoints depending on direction:
//
//       Top-up   POST /admin/agents/:id/wallet/topup
//       Withdraw POST /admin/agents/:id/wallet/withdraw
//
// ─── UX notes ─────────────────────────────────────────────────────
//   * Modal opens from the per-row kebab. Renders as a centred modal
//     on desktop, full-height sheet on phones — same primitive the
//     EntityActionSheet uses.
//   * Amount in INR; positive int only. Direction (credit/debit) is
//     a segmented toggle at the top; agent-source forces credit.
//   * The "reason" string lands in the wallet ledger note column AND
//     in the audit_log meta — searchable from the AdminMore audit
//     surface later.
//   * Submit is disabled while busy + while the inputs are invalid.

import { useEffect, useState } from 'react';
import { levelName } from '../lib/levelNames';
import { motion } from 'motion/react';
import { createPortal } from 'react-dom';
import AdminAvatar from '@baaligames/shared-ui/admin/components/AdminAvatar';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import ProofImagePicker from '@baaligames/shared-ui/components/ProofImagePicker';
import { INPUT_CLS } from './fieldStyles';

// 2026-05-16 — subject types now carry every identifier the modal
// needs to render a rich "who am I crediting?" header card. The
// header mirrors the search-dropdown row so admin recognises the
// exact entity they picked even when usernames collide (e.g.
// @agent2 vs @agent_2). Display-name + entity badge + level / UAID
// (agents) or ownership (players) + balance — all visible at a
// glance before they fill out the amount.
type PlayerSubject = {
  kind: 'player';
  id: number;
  username: string;
  /** Pretty display name from first+last. Falls back to username. */
  displayName?: string | null;
  avatarUrl?: string | null;
  /** Used to determine whether the "Agent" source toggle is selectable. */
  agentId: number | null;
  agentUsername: string | null;
  walletBalance: number;
};

type AgentSubject = {
  kind: 'agent';
  id: number;
  username: string;
  avatarUrl?: string | null;
  /** Chain depth (1 = direct, 2..7 = sub-agent depth). */
  level?: number | null;
  /** Public-shareable 8-char Unique Agent ID (R5). */
  uaid?: string | null;
  /** Parent agent's @username for the ownership chain caption. */
  parentUsername?: string | null;
  /** Parent agent id — lets the modal fetch parent.level + parent.pool
   *  so admin can see the upline context before they credit / debit.
   *  Null for admin-direct agents (no parent agent — admin is implicit
   *  counterparty, so no parent chip rendered). */
  parentAgentId?: number | null;
  poolBalance: number;
};

export type DirectAdjustSubject = PlayerSubject | AgentSubject;

type Direction = 'credit' | 'debit';
type PlayerSource = 'platform' | 'agent';
// Phase 17 — when the subject is an agent, admin chooses which POOL
// to move points to/from:
//   target='wallet'  → operating wallet (agent_wallet_ledger)
//   target='deposit' → security deposit collateral (agent_deposit_ledger)
// and which counter-party pool funds (credit) or receives (debit) the
// movement:
//   source/dest='admin'  → admin_wallet (manual_debit / manual_credit)
//   source/dest='parent' → parent agent's operating wallet (hidden
//                          for L1 agents whose parent_agent_id is null)
type AgentTarget = 'wallet' | 'deposit';
type AgentCounterparty = 'admin' | 'parent';

export default function DirectWalletAdjustModal({
  subject, onClose, onDone, defaultDirection = 'credit',
}: {
  subject: DirectAdjustSubject;
  onClose: () => void;
  onDone: () => void;
  /** Pre-select credit or debit when the modal opens. The user can
   *  still flip the toggle. Used by the Requests-tab DirectAdjustCard
   *  so the modal opens with the same intent as the tab they came
   *  from (Recharge tab → credit; Withdrawal tab → debit). */
  defaultDirection?: Direction;
}) {
  const [direction, setDirection]   = useState<Direction>(defaultDirection);
  // Source defaults to 'agent' when the player has an owning agent
  // (preserves the agent's ledger trail) and falls back to 'platform'
  // for orphaned players or when admin explicitly chooses.
  const isPlayer = subject.kind === 'player';
  const playerSource: PlayerSource =
    isPlayer && subject.agentId != null ? 'agent' : 'platform';
  const [source, setSource] = useState<PlayerSource>(playerSource);
  // Phase 17 — agent-subject only. Default to 'wallet' for backwards
  // compatibility (existing flow). 'deposit' targets the new endpoints
  // /admin/agents/:id/deposit/{topup,withdraw}.
  const [agentTarget, setAgentTarget] = useState<AgentTarget>('wallet');
  // Phase 17 — counter-party for agent-subject ops. For L1 agents
  // (parentAgentId == null) the "parent" option is hidden and we keep
  // this at 'admin'. Stored once and reused for both credit (source)
  // and debit (destination) since the semantics are the same pool.
  const agentHasParent = subject.kind === 'agent' && (subject.parentAgentId ?? null) != null;
  const [agentCounter, setAgentCounter] = useState<AgentCounterparty>('admin');
  const [amount, setAmount]   = useState<string>('');
  const [reason, setReason]   = useState<string>('');
  // Phase 18 — optional payment proof. Carried into the submit as
  // multipart/form-data when present; falls back to plain JSON when
  // skipped (old behaviour).
  const [proofBlob, setProofBlob] = useState<Blob | null>(null);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState<string | null>(null);

  // 2026-05-16 — fetch the parent agent's level + pool so the
  // identity card can surface that context. The parent is the
  // upline counterparty admin needs to sanity-check before either
  // funding a credit from the parent's pool (player + agent-source)
  // or simply understanding where this subject sits in the chain.
  //
  //   • player subject → parent is the owning agent (subject.agentId)
  //   • agent subject  → parent is subject.parentAgentId (null when
  //     the subject is admin-direct, in which case we render no
  //     parent chip — admin is the implicit counterparty).
  //
  // Best-effort: a 404 or permission error leaves parentLoading=false
  // and parent=null, and the chip is simply hidden. We don't block
  // the user on this — it's an information surface, not a gate.
  const parentAgentId: number | null = subject.kind === 'player'
    ? subject.agentId
    : (subject.parentAgentId ?? null);
  type ParentInfo = { id: number; username: string; level: number | null; poolBalance: number };
  const [parent, setParent] = useState<ParentInfo | null>(null);
  const [parentLoading, setParentLoading] = useState<boolean>(parentAgentId != null);

  useEffect(() => {
    let cancelled = false;
    if (parentAgentId == null) {
      setParent(null);
      setParentLoading(false);
      return;
    }
    setParentLoading(true);
    (async () => {
      try {
        const r = await adminApi.get<{ agent: { id: number; username: string; level: number | null; wallet_balance: number } }>(
          `/admin/agents/${parentAgentId}`
        );
        if (cancelled) return;
        setParent({
          id: r.agent.id,
          username: r.agent.username,
          level: r.agent.level ?? null,
          poolBalance: Number(r.agent.wallet_balance ?? 0),
        });
      } catch {
        if (!cancelled) setParent(null);
      } finally {
        if (!cancelled) setParentLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [parentAgentId]);

  const amountInt = (() => {
    const n = Number(amount);
    return Number.isFinite(n) && n > 0 && Math.floor(n) === n ? n : 0;
  })();
  const reasonOk = reason.trim().length > 0;
  const canSubmit = !busy && amountInt > 0 && reasonOk;

  // Agent-source debit isn't currently supported by the backend (the
  // recharge endpoint is one-way agent→player). Surface that clearly
  // so admin picks Platform debit + a separate agent withdraw.
  const agentDebitUnsupported = isPlayer && source === 'agent' && direction === 'debit';

  /**
   * Phase 18 — submit helper. Switches between JSON and
   * multipart/form-data based on whether a proof image was selected.
   * Multer on the server side accepts both — when no file is sent
   * the route handler sees `req.file === undefined` and falls back
   * to plain JSON behaviour.
   */
  async function sendAdjust(path: string, payload: Record<string, unknown>) {
    if (proofBlob) {
      const fd = new FormData();
      for (const [k, v] of Object.entries(payload)) {
        if (v === undefined || v === null) continue;
        fd.append(k, String(v));
      }
      fd.append('proof', proofBlob, 'screenshot.jpg');
      await adminApi.postForm(path, fd);
    } else {
      await adminApi.post(path, payload);
    }
  }

  async function submit() {
    if (!canSubmit || agentDebitUnsupported) return;
    setBusy(true); setErr(null);
    try {
      if (subject.kind === 'player') {
        if (source === 'platform') {
          // Admin → Player direct mint/burn. delta is signed.
          await sendAdjust(`/admin/users/${subject.id}/wallet`, {
            delta: direction === 'credit' ? amountInt : -amountInt,
            reason: reason.trim(),
            markAsRecharge: direction === 'credit',
          });
        } else {
          // Agent → Player atomic transfer.
          if (subject.agentId == null) throw new Error('Player has no owning agent');
          await sendAdjust(
            `/admin/agents/${subject.agentId}/players/${subject.id}/recharge`,
            { amount: amountInt, note: reason.trim() }
          );
        }
      } else {
        // subject.kind === 'agent'. Route by (target, direction).
        const path =
          agentTarget === 'wallet'
            ? (direction === 'credit'
                ? `/admin/agents/${subject.id}/wallet/topup`
                : `/admin/agents/${subject.id}/wallet/withdraw`)
            : (direction === 'credit'
                ? `/admin/agents/${subject.id}/deposit/topup`
                : `/admin/agents/${subject.id}/deposit/withdraw`);
        const body: Record<string, unknown> = { amount: amountInt, note: reason.trim() };
        if (direction === 'credit') body.source = agentCounter;
        else                        body.destination = agentCounter;
        await sendAdjust(path, body);
      }
      onDone();
    } catch (e) {
      setErr((e as ApiError).message || 'Failed');
    } finally {
      setBusy(false);
    }
  }

  // 2026-05-20 — aliased to canonical INPUT_CLS for dark-mode fix.
  const inputCls = INPUT_CLS;

  const modal = (
    <div className="fixed inset-0 z-[8100] grid place-items-end sm:place-items-center
                    bg-black/60 backdrop-blur-sm p-0 sm:p-4 overflow-y-auto"
         onClick={busy ? undefined : onClose}
         role="dialog" aria-modal="true">
      <motion.div
        initial={{ y: 24, opacity: 0, scale: 0.97 }}
        animate={{ y: 0,  opacity: 1, scale: 1 }}
        exit={{ y: 16, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md mx-auto rounded-t-2xl sm:rounded-3xl
                   bg-white dark:bg-arena-900
                   ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/50
                   shadow-[0_20px_60px_rgba(0,0,0,0.45)] overflow-hidden"
      >
        {/* ─── Header ──────────────────────────────────────────────
            Title strip with close button. The full identity card
            (avatar + entity badge + level/UAID/ownership + balance)
            lives in its own block below so the admin can scan exactly
            who they're about to credit/debit before they fill out the
            amount. */}
        <div className="px-5 py-3 border-b border-arena-200/40 dark:border-arena-700/40
                        bg-arena-50/40 dark:bg-arena-900/40
                        flex items-center gap-2">
          <span className="grid place-items-center w-8 h-8 rounded-xl shrink-0
                           bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200/60
                           dark:bg-violet-400/15 dark:text-violet-300 dark:ring-violet-400/30">
            <Icon name="account_balance_wallet" size={18} filled />
          </span>
          <div className="text-[15px] font-bold text-neutral-900 dark:text-white truncate flex-1">
            Direct wallet adjust
          </div>
          <button onClick={onClose} disabled={busy}
            className="grid place-items-center w-8 h-8 rounded-full text-arena-500 dark:text-neon-200/65
                       hover:bg-arena-100/60 dark:hover:bg-arena-700/60 disabled:opacity-40"
            aria-label="Close">
            <Icon name="close" size={18} />
          </button>
        </div>

        {/* ─── Identity card ───────────────────────────────────────
            Same shape as the row in the search dropdown that admin
            clicked, but slightly larger + with a prominent PLAYER /
            AGENT badge so there's zero ambiguity about which entity
            kind is in scope. This is the safety surface for "am I
            crediting the right person?" — placed BEFORE direction /
            source / amount inputs because if this is wrong, nothing
            below matters.
            The parent chip below carries the upline context (parent
            @username + level + pool) so admin sees who else is in
            the money path (and, when funding from agent pool,
            whether that pool can actually absorb the move). */}
        <div className="px-5 py-4 border-b border-arena-200/40 dark:border-arena-700/40 space-y-3">
          <SubjectIdentityCard subject={subject} />
          <ParentChip parent={parent} loading={parentLoading} hasParent={parentAgentId != null} />
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Direction segmented toggle */}
          <Segment label="Direction">
            <SegmentButton active={direction === 'credit'} onClick={() => setDirection('credit')} icon="arrow_upward" tone="emerald">
              Credit (add)
            </SegmentButton>
            <SegmentButton active={direction === 'debit'} onClick={() => setDirection('debit')} icon="arrow_downward" tone="rose">
              Debit (deduct)
            </SegmentButton>
          </Segment>

          {/* Phase 17 — agent-only: which POOL to move (wallet vs deposit). */}
          {subject.kind === 'agent' && (
            <Segment label="Target pool">
              <SegmentButton active={agentTarget === 'wallet'} onClick={() => setAgentTarget('wallet')} icon="account_balance_wallet" tone="cyan">
                Operating wallet
                <span className="block text-[10px] font-normal opacity-80 leading-tight">
                  Day-to-day balance (recharge / payouts)
                </span>
              </SegmentButton>
              <SegmentButton active={agentTarget === 'deposit'} onClick={() => setAgentTarget('deposit')} icon="savings" tone="emerald">
                Security deposit
                <span className="block text-[10px] font-normal opacity-80 leading-tight">
                  Collateral pool (settlement safety)
                </span>
              </SegmentButton>
            </Segment>
          )}

          {/* Phase 17 — agent-only: which counter-party pool moves the
              money. Hidden for L1 agents (no parent) — admin is the
              only valid counter-party. */}
          {subject.kind === 'agent' && agentHasParent && (
            <Segment label={direction === 'credit' ? 'Funding source' : 'Destination pool'}>
              <SegmentButton active={agentCounter === 'admin'} onClick={() => setAgentCounter('admin')} icon="apartment" tone="cyan">
                Admin wallet
                <span className="block text-[10px] font-normal opacity-80 leading-tight">
                  {direction === 'credit' ? 'House funds the credit' : 'Pulled into admin wallet'}
                </span>
              </SegmentButton>
              <SegmentButton active={agentCounter === 'parent'} onClick={() => setAgentCounter('parent')} icon="account_tree" tone="emerald">
                Parent agent
                <span className="block text-[10px] font-normal opacity-80 leading-tight">
                  {direction === 'credit'
                    ? `From @${subject.parentUsername ?? 'parent'}'s wallet`
                    : `Into @${subject.parentUsername ?? 'parent'}'s wallet`}
                </span>
              </SegmentButton>
            </Segment>
          )}

          {/* Source toggle — only relevant for players */}
          {subject.kind === 'player' && (
            <Segment label="Funding source">
              <SegmentButton
                active={source === 'platform'}
                onClick={() => setSource('platform')}
                icon="apartment" tone="cyan">
                Platform
                <span className="block text-[10px] font-normal opacity-80 leading-tight">
                  Admin → Player. No agent involvement.
                </span>
              </SegmentButton>
              <SegmentButton
                active={source === 'agent'}
                onClick={() => setSource('agent')}
                disabled={subject.agentId == null}
                icon="support_agent" tone="emerald">
                Agent pool
                <span className="block text-[10px] font-normal opacity-80 leading-tight">
                  {subject.agentUsername
                    ? `From @${subject.agentUsername}'s pool`
                    : 'Player has no owning agent'}
                </span>
              </SegmentButton>
            </Segment>
          )}

          {/* Agent-side debit warning — backend doesn't support debit
              via the agent recharge endpoint today. */}
          {agentDebitUnsupported && (
            <div className="rounded-xl bg-orange-50 dark:bg-orange-500/10 ring-1 ring-inset ring-orange-200/70 dark:ring-orange-400/30
                            text-orange-700 dark:text-orange-200 text-[12.5px] px-3 py-2 leading-snug">
              <Icon name="info" size={14} filled className="inline align-text-bottom mr-1" />
              Agent-pool debits aren't supported here. To claw funds from this player,
              use <b>Platform debit</b>, then top-up the agent's pool separately.
            </div>
          )}

          {/* Amount */}
          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">
              Amount (₹)
            </span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="1000"
              className={inputCls + ' font-mono tabular-nums text-[16px]'}
              autoFocus
            />
          </label>

          {/* Reason */}
          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">
              Reason / note
            </span>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Cash collected at office · ref 7421"
              maxLength={190}
              className={inputCls}
            />
            <span className="block text-[10.5px] text-arena-500 dark:text-neon-200/55 mt-1 leading-tight">
              Written to the wallet ledger + audit log. Make it future-you can read.
            </span>
          </label>

          {/* Phase 18 — optional payment proof for this manual op.
              The server creates a synthetic queue row regardless;
              if a screenshot is attached it gets stored against
              that row (subject to the same 72h auto-deletion and,
              for debits, 2-view limit on the recipient's side). */}
          <ProofImagePicker
            onFile={(b) => setProofBlob(b)}
            onClear={() => setProofBlob(null)}
            onError={(m) => setErr(m)}
            label={direction === 'credit'
              ? 'Upload screenshot of the payment (optional)'
              : 'Upload screenshot of the payout (optional)'}
            hint={direction === 'credit'
              ? 'Visible to the recipient via the queue History.'
              : 'Visible to the recipient — they can open it twice within 72 h.'}
          />

          {/* Summary line */}
          <div className="rounded-xl bg-arena-50/70 dark:bg-arena-800/40
                          ring-1 ring-inset ring-arena-200/55 dark:ring-arena-600/40
                          px-3 py-2 text-[12.5px] leading-snug">
            <SummaryLine
              direction={direction}
              subject={subject}
              source={source}
              amount={amountInt}
              agentTarget={agentTarget}
              agentCounter={agentCounter}
              agentHasParent={agentHasParent}
            />
          </div>

          {err && (
            <div className="rounded-lg bg-rose-50 dark:bg-rose-500/10 ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30 text-rose-700 dark:text-rose-200 text-sm px-3 py-2">
              {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-arena-200/40 dark:border-arena-700/40
                        bg-arena-50/30 dark:bg-arena-900/40
                        flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
            className="px-4 py-2 rounded-full text-[13px] font-semibold
                       bg-white dark:bg-arena-700/40
                       ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                       text-arena-700 dark:text-neon-200/75
                       hover:bg-arena-50 dark:hover:bg-arena-700/60">
            Cancel
          </button>
          <button onClick={submit} disabled={!canSubmit || agentDebitUnsupported}
            className={`px-4 py-2 rounded-full text-[13px] font-bold inline-flex items-center gap-1.5 text-white
              ${direction === 'credit'
                ? 'bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 shadow-[0_2px_10px_rgba(16,185,129,0.35)]'
                : 'bg-rose-600 hover:bg-rose-500 active:bg-rose-700 shadow-[0_2px_10px_rgba(244,63,94,0.35)]'}
              disabled:opacity-50 disabled:cursor-not-allowed`}>
            {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name={direction === 'credit' ? 'check' : 'remove'} size={14} />}
            {(() => {
              // Phase 17 — agent-subject button label now carries the
              // target pool so the operator can't mistake a deposit
              // move for a wallet move (or vice-versa) after they've
              // toggled the segmented control above.
              if (subject.kind === 'agent') {
                const t = agentTarget === 'wallet' ? 'wallet' : 'deposit';
                return direction === 'credit'
                  ? `Credit ${t} ₹${amountInt || 0}`
                  : `Debit ${t} ₹${amountInt || 0}`;
              }
              return direction === 'credit'
                ? `Credit ₹${amountInt || 0}`
                : `Debit ₹${amountInt || 0}`;
            })()}
          </button>
        </div>
      </motion.div>
    </div>
  );

  return createPortal(modal, document.body);
}

// ─── Helpers ───────────────────────────────────────────────────────

function Segment({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1.5 font-semibold">
        {label}
      </span>
      <div className="grid grid-cols-2 gap-1.5">{children}</div>
    </div>
  );
}

function SegmentButton({
  active, onClick, icon, tone, disabled = false, children,
}: {
  active: boolean;
  onClick: () => void;
  icon: string;
  tone: 'emerald' | 'rose' | 'cyan';
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const activeCls = {
    emerald: 'bg-emerald-600 text-white ring-1 ring-emerald-600 shadow-[0_2px_10px_rgba(16,185,129,0.35)]',
    rose:    'bg-rose-600 text-white ring-1 ring-rose-600 shadow-[0_2px_10px_rgba(244,63,94,0.35)]',
    cyan:    'bg-cyan-600 text-white ring-1 ring-cyan-600 shadow-[0_2px_10px_rgba(14,165,233,0.35)]',
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`px-3 py-2 rounded-xl text-left transition active:scale-[0.98]
        text-[12.5px] font-bold
        ${active
          ? activeCls
          : 'bg-white dark:bg-arena-800/60 text-arena-700 dark:text-neon-200/85 ring-1 ring-inset ring-arena-200/55 dark:ring-arena-600/40 hover:bg-arena-50 dark:hover:bg-arena-800'}
        ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
    >
      <span className="inline-flex items-center gap-1.5">
        <Icon name={icon} size={14} filled={active} />
        {typeof children === 'string' ? children : null}
      </span>
      {typeof children !== 'string' ? children : null}
    </button>
  );
}

function SummaryLine({
  direction, subject, source, amount, agentTarget, agentCounter, agentHasParent,
}: {
  direction: Direction;
  subject: DirectAdjustSubject;
  source: PlayerSource;
  amount: number;
  agentTarget: AgentTarget;
  agentCounter: AgentCounterparty;
  agentHasParent: boolean;
}) {
  if (amount <= 0) {
    return <span className="text-arena-500 dark:text-neon-200/55 italic">
      Enter an amount to preview the move.
    </span>;
  }
  const arrow = direction === 'credit' ? '↑' : '↓';
  if (subject.kind === 'agent') {
    const targetLabel = agentTarget === 'wallet' ? 'operating wallet' : 'security deposit';
    // For L1 (no parent) we always pivot off the admin wallet.
    const counterParty = !agentHasParent
      ? 'admin wallet'
      : (agentCounter === 'admin' ? 'admin wallet' : `@${subject.parentUsername ?? 'parent'}'s wallet`);
    return direction === 'credit' ? (
      <span><b className="text-emerald-700 dark:text-emerald-300">{arrow} ₹{amount.toLocaleString('en-IN')}</b>
        {' '}credited to <b>@{subject.username}</b>'s {targetLabel} from <b>{counterParty}</b>.
        {agentTarget === 'deposit' && (subject.parentAgentId == null) && (
          <span className="block text-[11px] text-emerald-700/80 dark:text-emerald-200/70 mt-0.5">
            L1 mirror: admin's L1 deposit liability grows by the same amount.
          </span>
        )}
      </span>
    ) : (
      <span><b className="text-rose-700 dark:text-rose-300">{arrow} ₹{amount.toLocaleString('en-IN')}</b>
        {' '}debited from <b>@{subject.username}</b>'s {targetLabel} into <b>{counterParty}</b>.
        Cannot push balance below zero.
        {agentTarget === 'deposit' && (subject.parentAgentId == null) && (
          <span className="block text-[11px] text-rose-700/80 dark:text-rose-200/70 mt-0.5">
            L1 mirror: admin's L1 deposit liability shrinks by the same amount.
          </span>
        )}
      </span>
    );
  }
  // player
  if (source === 'platform') {
    return direction === 'credit' ? (
      <span><b className="text-emerald-700 dark:text-emerald-300">{arrow} ₹{amount.toLocaleString('en-IN')}</b>
        {' '}minted to <b>@{subject.username}</b>'s wallet. Triggers referral payout if any. No agent debited.</span>
    ) : (
      <span><b className="text-rose-700 dark:text-rose-300">{arrow} ₹{amount.toLocaleString('en-IN')}</b>
        {' '}burned from <b>@{subject.username}</b>'s wallet. No agent credited.</span>
    );
  }
  // source === 'agent', direction credit (debit blocked elsewhere)
  return (
    <span>
      <b className="text-emerald-700 dark:text-emerald-300">{arrow} ₹{amount.toLocaleString('en-IN')}</b>
      {' '}moved from <b>@{subject.agentUsername}</b>'s pool to <b>@{subject.username}</b>'s wallet.
      Atomic transfer; respects commission_terms.recharge_player caps.
    </span>
  );
}

// ─── Subject identity card ────────────────────────────────────────
//
// Top-of-modal "who am I about to credit / debit?" card. Mirrors the
// search-dropdown row admin clicked but slightly larger + with an
// explicit entity-kind badge (PLAYER / AGENT) so the admin can't
// misread which entity table they're operating on. Defensive UX —
// usernames collide all the time across player and agent rosters
// (e.g. someone might have @raj as both a player and an agent), and
// a wrong credit here would land in the wrong wallet entirely.
function SubjectIdentityCard({ subject }: { subject: DirectAdjustSubject }) {
  if (subject.kind === 'player') {
    const displayName = subject.displayName || subject.username;
    return (
      <div className="flex items-start gap-3">
        <AdminAvatar url={subject.avatarUrl ?? null} username={subject.username} size="md" />
        <div className="min-w-0 flex-1">
          {/* Top line — display name + PLAYER entity badge. Display
              name is bigger + bolder than anything else in the card. */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[16px] sm:text-[17px] font-display font-extrabold text-neutral-900 dark:text-white truncate leading-tight">
              {displayName}
            </span>
            <EntityBadge kind="player" />
          </div>
          {/* @handle · #id — the unique identifiers, smaller / muted. */}
          <div className="text-[12px] text-arena-600 dark:text-neon-200/70 truncate mt-0.5">
            @{subject.username} <span className="text-arena-400 dark:text-neon-200/70">·</span> #{subject.id}
          </div>
          {/* Ownership chain — explicit "platform direct" or "under @X"
              so admin sees who else is in the money path. */}
          <div className="text-[11px] text-arena-600 dark:text-neon-200/65 truncate mt-1 inline-flex items-center gap-1">
            <Icon name="support_agent" size={11} className="text-emerald-700 dark:text-emerald-400" />
            {subject.agentUsername ? (
              <>under <span className="font-semibold text-arena-700 dark:text-neon-200/85">@{subject.agentUsername}</span></>
            ) : (
              <span className="italic text-arena-400 dark:text-neon-200/55">platform direct (no parent agent)</span>
            )}
          </div>
        </div>
        {/* Right column — wallet balance, prominent so admin can
            sanity-check the relative size of the move before clicking
            Credit / Debit. */}
        <div className="text-right shrink-0">
          <div className="text-[15px] font-display font-extrabold text-cyan-700 dark:text-cyan-300 tabular-nums leading-none">
            ₹{subject.walletBalance.toLocaleString('en-IN')}
          </div>
          <div className="text-[10px] text-arena-500 dark:text-neon-200/55 mt-0.5 leading-none">
            wallet
          </div>
        </div>
      </div>
    );
  }
  // Agent subject.
  return (
    <div className="flex items-start gap-3">
      <AdminAvatar url={subject.avatarUrl ?? null} username={subject.username} size="md" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[16px] sm:text-[17px] font-display font-extrabold text-neutral-900 dark:text-white truncate leading-tight">
            @{subject.username}
          </span>
          <EntityBadge kind="agent" />
          {subject.level != null && (
            <span className="shrink-0 text-[9.5px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-400/15 px-1.5 py-0.5 rounded">
              {levelName(subject.level)}
            </span>
          )}
        </div>
        <div className="text-[12px] text-arena-600 dark:text-neon-200/70 truncate mt-0.5">
          {subject.parentUsername ? `under @${subject.parentUsername}` : 'admin direct'}
          <span className="text-arena-400 dark:text-neon-200/70"> · #{subject.id}</span>
        </div>
        {subject.uaid && (
          <div className="text-[11px] text-arena-600 dark:text-neon-200/65 mt-1 inline-flex items-center gap-1 font-mono">
            <Icon name="qr_code_2" size={11} className="text-arena-500 dark:text-neon-200/65" />
            {subject.uaid}
          </div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className={`text-[15px] font-display font-extrabold tabular-nums leading-none
          ${subject.poolBalance >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-600 dark:text-red-400'}`}>
          ₹{subject.poolBalance.toLocaleString('en-IN')}
        </div>
        <div className="text-[10px] text-arena-500 dark:text-neon-200/55 mt-0.5 leading-none">
          pool
        </div>
      </div>
    </div>
  );
}

function EntityBadge({ kind }: { kind: 'player' | 'agent' }) {
  // Distinct colour per entity kind so the badge reads at a glance
  // even at thumbnail size. Cyan for players (matches player-flow
  // accents elsewhere), emerald for agents (matches agent-flow).
  const cls = kind === 'player'
    ? 'bg-cyan-600 text-white dark:bg-cyan-500 dark:text-white shadow-[0_1px_3px_rgba(14,165,233,0.4)]'
    : 'bg-emerald-600 text-white dark:bg-emerald-500 dark:text-white shadow-[0_1px_3px_rgba(16,185,129,0.4)]';
  return (
    <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[9.5px] font-extrabold uppercase tracking-[0.12em] whitespace-nowrap ${cls}`}>
      {kind === 'player' ? 'Player' : 'Agent'}
    </span>
  );
}

// ─── Parent chip ──────────────────────────────────────────────────
//
// Below the main identity card, when the subject has a parent agent,
// we show a "PARENT" chip with the parent's level + pool. Three
// states matter:
//
//   1. hasParent=false  → admin-direct agent OR platform-direct
//      player. Render nothing — admin is the implicit counterparty.
//   2. hasParent=true, loading=true → show a skeleton chip with
//      "Loading parent…" so admin knows the data is in flight (one
//      GET, usually <200ms but worth signalling).
//   3. hasParent=true, parent loaded → full chip with @username +
//      level pill + pool balance.
//
// We use a distinct surface (subtle amber tint) so the chip reads as
// "context about an OTHER entity in the money path" rather than
// being mistaken for a second identity card for the subject. The
// pool figure also uses red-when-negative so admin instantly sees
// when the parent's pool is underwater (relevant for player + agent-
// source recharges, where an underwater pool means the credit will
// 400 with Insufficient balance).
function ParentChip({
  parent, loading, hasParent,
}: {
  parent: { id: number; username: string; level: number | null; poolBalance: number } | null;
  loading: boolean;
  hasParent: boolean;
}) {
  if (!hasParent) return null;
  if (loading || !parent) {
    return (
      <div className="rounded-xl bg-amber-50/60 dark:bg-amber-500/10
                      ring-1 ring-inset ring-amber-200/50 dark:ring-amber-400/20
                      px-3 py-2 text-[12px] text-amber-700 dark:text-amber-300 inline-flex items-center gap-1.5">
        <Icon name="progress_activity" size={13} className="animate-spin" />
        Loading parent details…
      </div>
    );
  }
  const poolNeg = parent.poolBalance < 0;
  return (
    <div className="rounded-xl bg-amber-50/60 dark:bg-amber-500/10
                    ring-1 ring-inset ring-amber-200/50 dark:ring-amber-400/20
                    px-3 py-2 flex items-center gap-2">
      <span className="grid place-items-center w-7 h-7 rounded-full shrink-0
                       bg-amber-100 text-amber-800
                       dark:bg-amber-400/20 dark:text-amber-200">
        <Icon name="account_tree" size={14} filled />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[9.5px] font-extrabold uppercase tracking-[0.12em] text-amber-700 dark:text-amber-300 leading-none">
          Parent agent
        </div>
        <div className="text-[13px] font-semibold text-neutral-900 dark:text-white truncate leading-tight mt-1 inline-flex items-center gap-1.5">
          @{parent.username}
          {parent.level != null && (
            <span className="shrink-0 text-[9.5px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-400/15 px-1.5 py-0.5 rounded">
              {levelName(parent.level)}
            </span>
          )}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className={`text-[13px] font-display font-extrabold tabular-nums leading-none
          ${poolNeg ? 'text-red-600 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-300'}`}>
          ₹{parent.poolBalance.toLocaleString('en-IN')}
        </div>
        <div className="text-[9.5px] text-amber-700/80 dark:text-amber-200/70 leading-none mt-0.5">
          parent pool
        </div>
      </div>
    </div>
  );
}
